import { EllipsisHorizontalIcon } from '@heroicons/react/24/solid';
import { IconButton } from '@main/complykit/components/icon-button';
import { Menu, MenuItem, MenuProps } from '@main/complykit/components/menu';
import { isAbortError } from '@main/shared/utils';
import {
  ComponentProps,
  ComponentType,
  MouseEventHandler,
  ReactElement,
  Ref,
  SVGProps,
  useCallback,
  useState,
} from 'react';
import { Pressable } from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import type { MenuActionItem } from './types';

/**
 * The props the legacy `renderTrigger` escape receives. The ComplyKit Menu
 * seats its trigger through React Aria's press context, so the engine no
 * longer hands out an onClick to wire by hand; the fields stay optional for
 * the call sites that still destructure them.
 */
export interface TriggerButtonProps {
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

type RenderTriggerProps = TriggerButtonProps & {
  ref?: Ref<HTMLButtonElement>;
};

export interface MenuActionsProps<T = unknown> {
  actions: MenuActionItem<T>[] | undefined;
  /**
   * A ComplyKit interactive element (usually an IconButton) for the Menu's
   * trigger seat. It is seated through React Aria's trigger context, so it
   * needs no wiring and keeps its built-in label tooltip.
   */
  trigger?: ReactElement;
  /**
   * The legacy escape for triggers that are NOT ComplyKit elements (Chakra
   * buttons in the remaining app seats). The output is made pressable by the
   * engine and seated as the Menu trigger; the element must forward its ref
   * and spread unknown props to the DOM. Retires with the app-seat batch;
   * ComplyKit triggers use `trigger` instead (a React Aria element inside
   * this escape would be wired twice).
   */
  renderTrigger?: (props: RenderTriggerProps) => ReactElement;
  /** Menu-wide default for items that do not decide `closeOnSelect` themselves. */
  closeOnSelect?: boolean;
  isOpen?: boolean;
  extra?: T;
  placement?: MenuProps['placement'];
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * The action-menu engine: a `MenuActionItem[]` config rendered as a ComplyKit
 * Menu. The config shape is unchanged from the Chakra era and maps per
 * menu.md: label->children, colorScheme red->tone critical, isDisabled +
 * disabledTooltip->the unavailable form, closeOnSelect->closesOnSelect,
 * href/isExternal->href/target, and a promise-returning onClick drives the
 * item's pending spinner here in the engine.
 */
export function MenuActions<T>({
  actions,
  extra,
  trigger,
  renderTrigger,
  closeOnSelect = true,
  isOpen,
  placement,
  onOpen,
  onClose,
}: MenuActionsProps<T>) {
  const { t } = useTranslation('ui');
  const [internalOpen, setInternalOpen] = useState(false);
  const [pendingActionLabel, setPendingActionLabel] = useState<string>();
  const isControlled = isOpen !== undefined;
  const isMenuOpen = isControlled ? isOpen : internalOpen;

  const setMenuOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      if (nextOpen) {
        onOpen?.();
        return;
      }

      onClose?.();
    },
    [isControlled, onClose, onOpen],
  );

  if (!actions?.length) {
    return null;
  }

  const seatedTrigger =
    trigger ??
    (renderTrigger ? (
      /* Pressable is how React Aria seats a non-RAC element: it consumes the
       * menu trigger's press context and clones the wiring (handlers, ref,
       * aria attributes) onto the rendered element. The cast is because
       * Pressable types its child against React Aria's DOMAttributes, while
       * the escape's contract is just "forwards ref, spreads to the DOM". */
      <Pressable>{renderTrigger({}) as ComponentProps<typeof Pressable>['children']}</Pressable>
    ) : (
      <IconButton icon={EllipsisHorizontalIcon} label={t('drawer.actions.more')} />
    ));

  return (
    <Menu
      trigger={seatedTrigger}
      placement={placement}
      isOpen={isMenuOpen}
      onOpenChange={setMenuOpen}
    >
      {actions.map((action) => {
        const item = (
          <ActionMenuItem
            key={action.label}
            action={action}
            closeOnSelectDefault={closeOnSelect}
            isPending={action.isLoading || pendingActionLabel === action.label}
            onPendingChange={setPendingActionLabel}
            onOpenChange={setMenuOpen}
          />
        );

        /* The engine escape: the call site decorates the item element itself
         * (the bulk bar's field editors were the one consumer; they stay on
         * the frozen Chakra engine until the Popover round). */
        if ('wrapper' in action && typeof action.wrapper === 'function') {
          return action.wrapper(item, extra as T);
        }

        return item;
      })}
    </Menu>
  );
}

function ActionMenuItem({
  action,
  closeOnSelectDefault,
  isPending,
  onPendingChange,
  onOpenChange,
}: {
  action: MenuActionItem;
  closeOnSelectDefault: boolean;
  isPending: boolean | undefined;
  onPendingChange: (label?: string) => void;
  onOpenChange: (open: boolean) => void;
}) {
  // import.meta.env, not process.env: this runs in the Vite browser build,
  // where process does not exist (vitest under Node has both).
  if (import.meta.env.DEV) {
    const colorScheme = action.colorScheme as string | undefined;
    if (colorScheme && colorScheme !== 'red') {
      // eslint-disable-next-line no-console
      console.warn(
        `MenuActions: colorScheme "${colorScheme}" has no ComplyKit mapping; only "red" (tone="critical") exists on a menu item.`,
      );
    }
  }

  const icon = action.icon as ComponentType<SVGProps<SVGSVGElement>>;
  const tone = action.colorScheme === 'red' ? ('critical' as const) : undefined;
  const closesOnSelect = action.closeOnSelect ?? closeOnSelectDefault;
  const disabledReason = action.isDisabled ? action.disabledTooltip : undefined;

  if ('href' in action) {
    return (
      <MenuItem
        icon={icon}
        tone={tone}
        isDisabled={action.isDisabled}
        disabledReason={disabledReason}
        closesOnSelect={closesOnSelect}
        href={action.href}
        target={action.isExternal ? '_blank' : undefined}
      >
        {action.label}
      </MenuItem>
    );
  }

  const handleAction =
    'onClick' in action
      ? () => {
          const result = action.onClick();

          if (!isPromiseLike(result)) {
            return;
          }

          onPendingChange(action.label);

          void result
            .finally(() => {
              onPendingChange(undefined);

              /* A repeatable action held the menu open through its pending
               * state; settling is what closes it. */
              if (closesOnSelect === false) {
                onOpenChange(false);
              }
            })
            .catch((error) => {
              if (!isAbortError(error)) {
                throw error;
              }
            });
        }
      : undefined;

  return (
    <MenuItem
      icon={icon}
      tone={tone}
      isDisabled={action.isDisabled}
      disabledReason={disabledReason}
      closesOnSelect={closesOnSelect}
      isPending={isPending}
      onAction={handleAction}
    >
      {action.label}
    </MenuItem>
  );
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'then' in value &&
    typeof value.then === 'function'
  );
}
